import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Key } from 'antd/lib/table/interface';
import { Form, ProjectsConstructorForm } from '@/components';
import { AddEmployeeDto, ProjectDto } from '@/core';
import { usePositionsOptions, useDepartmentsTreeData, useSchemaEmployee, FormValueType } from './hooks';
import { getTreeKey } from './helpers';
import classes from './EmployeeForm.module.scss';

type PropsType = {
  initialValue: FormValueType;
  formUid: string;
  handleCancel?: () => void;
  handleSubmit: (employeeData: AddEmployeeDto) => void;
  isCancelButton?: boolean;
  projectData: ProjectDto;
  isTooltip?: boolean;
};

export const EmployeeForm = ({
  initialValue,
  formUid,
  handleCancel,
  handleSubmit,
  isCancelButton,
  projectData,
  isTooltip = false,
}: PropsType) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const positionsOptions = usePositionsOptions(projectData);
  const departmentsTreeData = useDepartmentsTreeData();
  const schemaEmployee = useSchemaEmployee(projectData, initialValue);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([getTreeKey(initialValue)]);

  const onTreeExpand = (keys: Key[]) => setExpandedKeys(keys);
  const onSubmit = (value: AddEmployeeDto) => {
    setExpandedKeys([]);
    handleSubmit(value);
  };

  return (
    <ProjectsConstructorForm<FormValueType>
      handleCancel={handleCancel}
      handleSubmit={onSubmit}
      schema={schemaEmployee}
      initialValues={initialValue}
      formUid={formUid}
      isCancelButton={isCancelButton}
    >
      <div className={classes.nameInput}>
        <Form.Input
          label={false}
          name="name"
          allowClear
          required
          placeholder={t('addEmployees.placeholder.name')}
          suffix={
            isTooltip && (
              <Tooltip title={t('addEmployees.employee.tooltip')}>
                <InfoCircleOutlined className={classes.nameInputTooltipIcon} />
              </Tooltip>
            )
          }
        />
      </div>
      <div className={classes.positionNameSelect}>
        <Form.Select
          label={false}
          options={positionsOptions}
          name="positionId"
          placeholder={t('addEmployees.placeholder.positionName')}
          required
        />
      </div>
      <div className={classes.departmentSelect}>
        <Form.TreeSelect
          label={false}
          treeData={departmentsTreeData}
          name="department"
          placeholder={t('addEmployees.placeholder.department')}
          treeExpandedKeys={expandedKeys}
          onTreeExpand={onTreeExpand}
          required
        />
      </div>
    </ProjectsConstructorForm>
  );
};
